/**
 * .tuMetrics {"tu1": 0.9, "tu2": 0.92, "tu3": 3, "notes": "Pure card row mapping; 0 any", "tuVersion": "2.5"}
 * .help "Maps cards table rows to Card and Partial<Card> updates to snake_case DB payloads. No I/O."
 */
import type { Card, CardType, SentimentVotes, InlineMediaItem } from '../../../types/index.ts';
import { normalizeCardStatus } from '../../../utils/normalizeCardStatus.ts';

type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]; // Define Json type locally

// July 2026: types/supabase.ts (generated Database types) is an empty file, so
// the old `Database['public']['Tables']['cards']` lookups never type-checked.
// Hand-written row shape until codegen is wired up.
export interface CardRow {
  id: string;
  type: string;
  title: string;
  description: string | null;
  status: string | null;
  priority: Card['priority'];
  assignee_id: string | null;
  created_by_id: string | null;
  requested_by_id: string | null;
  created_at: string;
  due_date: string | null;
  reminder_days: Card['reminderDays'] | null;
  complexity: Card['complexity'] | null;
  time_estimate_days: number | null;
  blocked: boolean;
  blocked_reason: string | null;
  blocked_at: string | null;
  is_recurring: boolean | null;
  recurrence_pattern: Json | null;
  next_occurrence_date: string | null;
  approval_workflow: Json | null;
  tags: Json | null;
  depends_on: string[];
  blocked_by: string[] | null;
  sprint_id: string | null;
  roadmap_id: string | null;
  converted_from_id: string | null;
  sentiment_votes: Json | null;
  github_pr: Json | null;
  github_issue: Json | null;
  github_commits: Json | null;
  github_branch: Json | null;
  last_activity: Json | null;
  automation_rules: Json | null;
  team_id: string | null;
  position: number | null;
  company_id: string | null;
  project_id: string | null;
  ticket_number: number | null;
  parent_card_id: string | null;
  started_at: string | null;
  ended_at: string | null;
  total_work_seconds: number | null;
  paused_at: string | null;
  last_work_session_start: string | null;
  customer_id: string | null;
  inline_media: Json | null;
  is_draft?: boolean | null;
  scheduled_date?: string | null;
  scheduled_start_time?: string | null;
  scheduled_duration_minutes?: number | string | null;
  auto_move_to_in_progress?: boolean | null;
  archived_at?: string | null;
  archived_by?: string | null;
  estimate_line_id?: string | null;
  estimated_quantity?: number | string | null;
  installed_quantity?: number | string | null;
  estimated_unit_labor_min?: number | string | null;
  estimated_unit_of_measure?: string | null;
  estimated_unit_cost?: number | string | null;
  estimated_total_cost?: number | string | null;
  estimated_bid_amount?: number | string | null;
  estimated_total_labor_hrs?: number | string | null;
}

export type CardInsert = Partial<CardDbFields> & {
  type: string;
  title: string;
};

export type CardUpdate = Partial<CardDbFields>;

interface CardDbFields {
  type: string;
  title: string;
  description: string | null;
  status: string | null;
  priority: Card['priority'];
  assignee_id: string | null;
  created_by_id: string | null;
  requested_by_id: string | null;
  due_date: string | null;
  reminder_days: Card['reminderDays'] | null;
  scheduled_date: string | null;
  scheduled_start_time: string | null;
  scheduled_duration_minutes: number | null;
  complexity: Card['complexity'] | null;
  time_estimate_days: number | null;
  blocked: boolean;
  blocked_reason: string | null;
  blocked_at: string | null;
  is_recurring: boolean;
  recurrence_pattern: string | Json | null;
  next_occurrence_date: string | null;
  approval_workflow: string | Json | null;
  tags: string[] | Json | null;
  depends_on: string[];
  blocked_by: string[] | null;
  sprint_id: string | null;
  roadmap_id: string | null;
  converted_from_id: string | null;
  sentiment_votes: Json | string | null;
  github_pr: Json | string | null;
  github_issue: Json | string | null;
  last_activity: string | Json | null;
  automation_rules: Json | string | null;
  team_id: string | null;
  position: number | null;
  company_id: string | null;
  project_id: string | null;
  parent_card_id: string | null;
  started_at: string | null;
  ended_at: string | null;
  total_work_seconds: number | null;
  paused_at: string | null;
  last_work_session_start: string | null;
  customer_id: string | null;
  inline_media: Json | null;
  is_draft: boolean;
  estimate_line_id: string | null;
  estimated_quantity: number | null;
  installed_quantity: number | null;
  estimated_unit_labor_min: number | null;
  estimated_unit_of_measure: string | null;
  estimated_unit_cost: number | null;
  estimated_total_cost: number | null;
  estimated_bid_amount: number | null;
  estimated_total_labor_hrs: number | null;
}

function optionalNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

const emptySentiment = (): SentimentVotes => ({ up: 0, down: 0, user_votes: [] });

export function parseSentimentVotes(sentimentVotes: unknown): SentimentVotes {
  if (sentimentVotes == null) return emptySentiment();

  let parsed: unknown = sentimentVotes;
  if (typeof sentimentVotes === 'string') {
    try {
      parsed = JSON.parse(sentimentVotes);
    } catch {
      return emptySentiment();
    }
  }
  if (typeof parsed !== 'object' || parsed === null) return emptySentiment();
  const record = parsed as Record<string, unknown>;

  if (record.user_votes) {
    return {
      up: typeof record.up === 'number' ? record.up : 0,
      down: typeof record.down === 'number' ? record.down : 0,
      user_votes: Array.isArray(record.user_votes) ? record.user_votes : [],
    };
  }
  if (record.votedBy) {
    return {
      up: typeof record.up === 'number' ? record.up : 0,
      down: typeof record.down === 'number' ? record.down : 0,
      user_votes: Array.isArray(record.votedBy)
        ? record.votedBy.filter((id): id is string => typeof id === 'string').map((userId) => ({
            user_id: userId,
            type: 'up' as const,
          }))
        : [],
    };
  }

  return emptySentiment();
}

// Helper function to safely parse JSONB data
const safeParseJsonb = <T>(jsonbData: Json | null): T | undefined => {
  if (jsonbData === null || jsonbData === undefined) {
    return undefined;
  }
  try {
    // If it's a string, parse it. Otherwise, assume it's already an object/array.
    return (typeof jsonbData === 'string'
      ? JSON.parse(jsonbData)
      : jsonbData) as T;
  } catch (e) {
    console.error('Error parsing JSONB data:', e, 'Data:', jsonbData);
    return undefined;
  }
};

// NEW: Helper function to safely parse JSONB data specifically for arrays
const safeParseArrayJsonb = <T>(jsonbData: Json | null): T[] => {
  if (jsonbData === null || jsonbData === undefined) {
    return [];
  }
  try {
    const parsed =
      typeof jsonbData === 'string' ? JSON.parse(jsonbData) : jsonbData;
    if (Array.isArray(parsed)) {
      return parsed as T[];
    }
    console.warn('Expected array but received non-array data:', parsed);
    return [];
  } catch (e) {
    console.error('Error parsing array JSONB data:', e, 'Data:', jsonbData);
    return [];
  }
};

export const mapCardFromDB = (row: CardRow): Card => {
  // Safely parse and default properties for githubPR
  const githubPR = safeParseJsonb<{
    id: string;
    number: number;
    url: string;
    title: string;
    status: 'open' | 'merged' | 'closed';
    branch: string;
    repo: string;
    author?: string;
    createdAt?: string;
    mergedAt?: string;
    commits?: string[];
  }>(row.github_pr);

  const mappedGithubPR = githubPR
    ? {
        id: githubPR.id || '',
        number: githubPR.number || 0,
        url: githubPR.url || '',
        title: githubPR.title || '',
        status: githubPR.status || 'open', // Default to 'open' if null
        branch: githubPR.branch || '',
        repo: githubPR.repo || '',
        author: githubPR.author,
        createdAt: githubPR.createdAt,
        mergedAt: githubPR.mergedAt,
        commits: githubPR.commits || [], // Ensure commits is an array
      }
    : undefined;

  // Safely parse and default properties for githubIssue
  const githubIssue = safeParseJsonb<{
    id: string;
    url: string;
    number: number;
    status: 'open' | 'closed';
    title: string;
    repo?: string;
  }>(row.github_issue);

  const mappedGithubIssue = githubIssue
    ? {
        id: githubIssue.id || '',
        url: githubIssue.url || '',
        number: githubIssue.number || 0, // Default to 0 if null
        status: githubIssue.status || 'open', // Default to 'open' if null
        title: githubIssue.title || '',
        repo: githubIssue.repo,
      }
    : undefined;

  // Safely parse and default properties for githubCommits
  const githubCommits = safeParseArrayJsonb<{
    sha: string;
    message: string;
    url: string;
    author: string;
    timestamp: string;
    branch: string;
    repository: string;
  }>(row.github_commits);

  // Safely parse and default properties for githubBranch
  const githubBranch = safeParseJsonb<{
    name: string;
    url: string;
    repo: string;
    created_at: string;
    last_commit_sha?: string;
  }>(row.github_branch);

  // Safely parse and default properties for lastActivity
  const lastActivity = safeParseJsonb<{
    type: 'commit' | 'pr_update' | 'comment' | 'status_change';
    timestamp: string;
    userId: string;
    details: string;
  }>(row.last_activity);

  const mappedLastActivity = lastActivity
    ? {
        type: lastActivity.type || 'comment', // Default type
        timestamp: lastActivity.timestamp || '',
        userId: lastActivity.userId || '',
        details: lastActivity.details || '',
      }
    : undefined;

  // Safely parse and default properties for automationRules
  const automationRules = safeParseJsonb<{
    onPRMerge?: 'move_to_done' | 'notify_only';
    onBranchCreate?: 'move_to_progress' | 'notify_only';
    onIssueClose?: 'move_to_done' | 'notify_only';
  }>(row.automation_rules);

  const mappedAutomationRules = automationRules
    ? {
        onPRMerge: automationRules.onPRMerge || undefined,
        onBranchCreate: automationRules.onBranchCreate || undefined,
        onIssueClose: automationRules.onIssueClose || undefined,
      }
    : undefined;

  return {
    id: row.id.trim(),
    type: row.type as CardType,
    title: row.title,
    description: row.description || '',
    status: normalizeCardStatus(row.status) ?? 'todo',
    priority: row.priority,
    assigneeId: row.assignee_id?.trim() || undefined,
    createdById: row.created_by_id?.trim() || '',
    requestedById: row.requested_by_id?.trim() || undefined, // Only when agent creates for a human
    createdAt: row.created_at,
    dueDate: row.due_date || undefined,
    reminderDays: row.reminder_days || undefined,
    scheduledDate: row.scheduled_date ?? undefined,
    scheduledStartTime: row.scheduled_start_time ?? undefined,
    scheduledDurationMinutes: optionalNumber(row.scheduled_duration_minutes),
    // auto_move_to_in_progress: never shipped UI; DB column ignored on read
    complexity: row.complexity || undefined,
    timeEstimateDays: row.time_estimate_days || undefined,
    blocked: row.blocked,
    blockedReason: row.blocked_reason || undefined,
    blockedAt: row.blocked_at || undefined,
    isRecurring: row.is_recurring || false,
    recurrencePattern: safeParseJsonb(row.recurrence_pattern),
    nextOccurrenceDate: row.next_occurrence_date || undefined,
    approvalWorkflow: safeParseJsonb(row.approval_workflow),
    tags: safeParseArrayJsonb<string>(row.tags),
    dependsOn: row.depends_on,
    blockedBy: row.blocked_by || [],
    sprintId: row.sprint_id || undefined,
    roadmapId: row.roadmap_id || undefined,
    convertedFromId: row.converted_from_id || undefined,
    sentimentVotes: parseSentimentVotes(row.sentiment_votes),
    githubPR: mappedGithubPR,
    githubIssue: mappedGithubIssue,
    githubCommits: githubCommits || undefined,
    githubBranch: githubBranch || undefined,
    lastActivity: mappedLastActivity,
    automationRules: mappedAutomationRules,
    teamId: row.team_id || undefined,
    position: row.position || undefined,
    companyId: row.company_id || undefined,
    projectId: row.project_id || undefined,
    ticketNumber: row.ticket_number || undefined,
    parentCardId: row.parent_card_id || undefined,
    // Time Tracking (January 15, 2025)
    startedAt: row.started_at || undefined,
    endedAt: row.ended_at || undefined,
    totalWorkSeconds: row.total_work_seconds || undefined,
    pausedAt: row.paused_at || undefined,
    lastWorkSessionStart: row.last_work_session_start || undefined,
    // Customer Billing (January 15, 2025 - Future Feature)
    customerId: row.customer_id || undefined,
    // Inline Media (November 30, 2025: Rich descriptions)
    inline_media: safeParseArrayJsonb<InlineMediaItem>(row.inline_media),
    // December 4, 2025: Archive support
    archivedAt: row.archived_at || undefined,
    archivedBy: row.archived_by || undefined,
    is_draft: row.is_draft || false,
    estimateLineId: row.estimate_line_id ?? undefined,
    estimatedQuantity: optionalNumber(row.estimated_quantity),
    installedQuantity: optionalNumber(row.installed_quantity),
    estimatedUnitLaborMin: optionalNumber(row.estimated_unit_labor_min),
    estimatedUnitOfMeasure: row.estimated_unit_of_measure ?? undefined,
    estimatedUnitCost: optionalNumber(row.estimated_unit_cost),
    estimatedTotalCost: optionalNumber(row.estimated_total_cost),
    estimatedBidAmount: optionalNumber(row.estimated_bid_amount),
    estimatedTotalLaborHrs: optionalNumber(row.estimated_total_labor_hrs),
  };
};

export const mapCardToDB = (
  card: Omit<Card, 'id' | 'createdAt'>,
  companyId?: string
): CardInsert => ({
  type: card.type,
  title: card.title,
  description: card.description,
  status: card.status,
  priority: card.priority,
  assignee_id: card.assigneeId || null,
  created_by_id: card.createdById,
  // Only set when an agent creates on behalf of a human; otherwise null
  requested_by_id: card.requestedById || null,
  due_date: card.dueDate || null,
  reminder_days: card.reminderDays || null, // December 5, 2025: Task reminders
  scheduled_date: card.scheduledDate || null,
  scheduled_start_time: card.scheduledStartTime || null,
  scheduled_duration_minutes: card.scheduledDurationMinutes ?? null,
  complexity: card.complexity || null,
  time_estimate_days: card.timeEstimateDays || null,
  blocked: card.blocked,
  blocked_reason: card.blockedReason || null,
  blocked_at: card.blockedAt || null,
  is_recurring: card.isRecurring || false,
  recurrence_pattern: card.recurrencePattern
    ? JSON.stringify(card.recurrencePattern)
    : null,
  next_occurrence_date: card.nextOccurrenceDate || null,
  approval_workflow: card.approvalWorkflow
    ? JSON.stringify(card.approvalWorkflow)
    : null,
  tags: card.tags,
  depends_on: card.dependsOn,
  blocked_by: card.blockedBy?.length ? card.blockedBy : null,
  sprint_id: card.sprintId || null,
  roadmap_id: card.roadmapId || null,
  // Suspected bug: reads snake_case `converted_from_id`, which is not on the Card
  // type (camelCase is `convertedFromId`), so this is undefined for typed callers.
  // Kept as-is to preserve runtime behavior; widened access fixes the type only.
  converted_from_id: (card as Partial<Card> & { converted_from_id?: string }).converted_from_id || null,
  sentiment_votes: card.sentimentVotes
    ? JSON.stringify(card.sentimentVotes)
    : null,
  github_pr: card.githubPR ? JSON.stringify(card.githubPR) : null,
  github_issue: card.githubIssue ? JSON.stringify(card.githubIssue) : null,
  last_activity: card.lastActivity ? JSON.stringify(card.lastActivity) : null,
  automation_rules: card.automationRules
    ? JSON.stringify(card.automationRules)
    : null,
  team_id: card.teamId || null,
  position: card.position || null,
  company_id: companyId || null,
  project_id: card.projectId || null,
  parent_card_id: card.parentCardId || null,
  is_draft: card.is_draft || false, // December 9, 2025: Draft support
  // Estimate snapshot columns set via updateCardById after build (not on insert; DB types may not include until migration)
});

export const mapCardUpdatesToDB = (updates: Partial<Card>): CardUpdate => {
  const dbUpdates: CardUpdate = {};
  if (updates.type !== undefined) dbUpdates.type = updates.type;
  if (updates.title !== undefined) dbUpdates.title = updates.title;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.priority !== undefined) dbUpdates.priority = updates.priority;
  if (updates.assigneeId !== undefined) dbUpdates.assignee_id = updates.assigneeId;
  if (updates.createdById !== undefined) dbUpdates.created_by_id = updates.createdById;
  if (updates.requestedById !== undefined) dbUpdates.requested_by_id = updates.requestedById; // only when explicitly set
  // Convert empty strings to null for timestamp fields
  if (updates.dueDate !== undefined) {
    dbUpdates.due_date = (updates.dueDate && typeof updates.dueDate === 'string' && updates.dueDate.trim() !== '') 
      ? updates.dueDate 
      : null;
  }
  // December 5, 2025: Task reminders
  if (updates.reminderDays !== undefined) {
    dbUpdates.reminder_days = updates.reminderDays || null;
  }
  // Task scheduling (legacy Now planned blocks — no card UI writer)
  if (updates.scheduledDate !== undefined) {
    dbUpdates.scheduled_date = (updates.scheduledDate && updates.scheduledDate.trim() !== '') ? updates.scheduledDate : null;
  }
  if (updates.scheduledStartTime !== undefined) {
    dbUpdates.scheduled_start_time = (updates.scheduledStartTime && updates.scheduledStartTime.trim() !== '') ? updates.scheduledStartTime : null;
  }
  if (updates.scheduledDurationMinutes !== undefined) {
    dbUpdates.scheduled_duration_minutes = updates.scheduledDurationMinutes != null && updates.scheduledDurationMinutes > 0 ? updates.scheduledDurationMinutes : null;
  }
  if (updates.complexity !== undefined) dbUpdates.complexity = updates.complexity;
  if (updates.timeEstimateDays !== undefined)
    dbUpdates.time_estimate_days = updates.timeEstimateDays;
  if (updates.blocked !== undefined) dbUpdates.blocked = updates.blocked;
  if (updates.blockedReason !== undefined) dbUpdates.blocked_reason = updates.blockedReason;
  if (updates.blockedAt !== undefined) {
    dbUpdates.blocked_at = (updates.blockedAt && typeof updates.blockedAt === 'string' && updates.blockedAt.trim() !== '') 
      ? updates.blockedAt 
      : null;
  }
  if (updates.isRecurring !== undefined) dbUpdates.is_recurring = updates.isRecurring;
  if (updates.recurrencePattern !== undefined)
    dbUpdates.recurrence_pattern = updates.recurrencePattern
      ? JSON.stringify(updates.recurrencePattern)
      : null;
  if (updates.nextOccurrenceDate !== undefined) {
    dbUpdates.next_occurrence_date = (updates.nextOccurrenceDate && typeof updates.nextOccurrenceDate === 'string' && updates.nextOccurrenceDate.trim() !== '') 
      ? updates.nextOccurrenceDate 
      : null;
  }
  if (updates.approvalWorkflow !== undefined)
    dbUpdates.approval_workflow = updates.approvalWorkflow
      ? JSON.stringify(updates.approvalWorkflow)
      : null;
  if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
  if (updates.dependsOn !== undefined) dbUpdates.depends_on = updates.dependsOn;
  if (updates.blockedBy !== undefined) dbUpdates.blocked_by = updates.blockedBy;
  // Inline Media (November 30, 2025: Rich descriptions)
  if (updates.inline_media !== undefined) dbUpdates.inline_media = updates.inline_media;
  if (updates.sprintId !== undefined) dbUpdates.sprint_id = updates.sprintId;
  if (updates.roadmapId !== undefined) dbUpdates.roadmap_id = updates.roadmapId;
  if (updates.convertedFromId !== undefined) dbUpdates.converted_from_id = updates.convertedFromId;
  if (updates.sentimentVotes !== undefined) dbUpdates.sentiment_votes = updates.sentimentVotes;
  if (updates.githubPR !== undefined) dbUpdates.github_pr = updates.githubPR;
  // Handle githubIssue: undefined/null means unlink (set to null in DB)
  // When linking, pass the object directly (Supabase handles JSONB automatically)
  // When unlinking, explicitly set to null
  if (updates.githubIssue !== undefined) {
    // If githubIssue is null or undefined, set to null in DB to unlink
    // Otherwise, use the object (Supabase will handle JSONB conversion)
    dbUpdates.github_issue = (updates.githubIssue === null || updates.githubIssue === undefined) 
      ? null 
      : updates.githubIssue;
  }
  if (updates.lastActivity !== undefined) {
    dbUpdates.last_activity = updates.lastActivity 
      ? JSON.stringify(updates.lastActivity) 
      : null;
  }
  if (updates.automationRules !== undefined)
    dbUpdates.automation_rules = updates.automationRules;
  if (updates.teamId !== undefined) dbUpdates.team_id = updates.teamId;
  if (updates.position !== undefined) dbUpdates.position = updates.position;
  if (updates.companyId !== undefined) dbUpdates.company_id = updates.companyId;
  if (updates.projectId !== undefined) dbUpdates.project_id = updates.projectId;
  if (updates.parentCardId !== undefined) dbUpdates.parent_card_id = updates.parentCardId;
  // Time Tracking (January 15, 2025)
  // Ensure timestamp fields are valid ISO strings or null (never empty strings or undefined)
  if (updates.startedAt !== undefined) {
    dbUpdates.started_at = (updates.startedAt && typeof updates.startedAt === 'string' && updates.startedAt.trim() !== '') 
      ? updates.startedAt 
      : null;
  }
  if (updates.endedAt !== undefined) {
    dbUpdates.ended_at = (updates.endedAt && typeof updates.endedAt === 'string' && updates.endedAt.trim() !== '') 
      ? updates.endedAt 
      : null;
  }
  if (updates.totalWorkSeconds !== undefined) dbUpdates.total_work_seconds = updates.totalWorkSeconds || null;
  // December 9, 2025: Draft support
  if (updates.is_draft !== undefined) dbUpdates.is_draft = updates.is_draft;
  // Project estimate snapshot
  if (updates.estimateLineId !== undefined) dbUpdates.estimate_line_id = updates.estimateLineId;
  if (updates.estimatedQuantity !== undefined) dbUpdates.estimated_quantity = updates.estimatedQuantity;
  if (updates.installedQuantity !== undefined) dbUpdates.installed_quantity = updates.installedQuantity;
  if (updates.estimatedUnitLaborMin !== undefined) dbUpdates.estimated_unit_labor_min = updates.estimatedUnitLaborMin;
  if (updates.estimatedUnitOfMeasure !== undefined) dbUpdates.estimated_unit_of_measure = updates.estimatedUnitOfMeasure;
  if (updates.estimatedUnitCost !== undefined) dbUpdates.estimated_unit_cost = updates.estimatedUnitCost;
  if (updates.estimatedTotalCost !== undefined) dbUpdates.estimated_total_cost = updates.estimatedTotalCost;
  if (updates.estimatedBidAmount !== undefined) dbUpdates.estimated_bid_amount = updates.estimatedBidAmount;
  if (updates.estimatedTotalLaborHrs !== undefined) dbUpdates.estimated_total_labor_hrs = updates.estimatedTotalLaborHrs;
  if (updates.pausedAt !== undefined) {
    dbUpdates.paused_at = (updates.pausedAt && typeof updates.pausedAt === 'string' && updates.pausedAt.trim() !== '') 
      ? updates.pausedAt 
      : null;
  }
  if (updates.lastWorkSessionStart !== undefined) {
    dbUpdates.last_work_session_start = (updates.lastWorkSessionStart && typeof updates.lastWorkSessionStart === 'string' && updates.lastWorkSessionStart.trim() !== '') 
      ? updates.lastWorkSessionStart 
      : null;
  }
  // Customer Billing (January 15, 2025 - Future Feature)
  if (updates.customerId !== undefined) dbUpdates.customer_id = updates.customerId || null;
  return dbUpdates;
};
